import React, { useState, useEffect } from "react";
import {
    FaKey,
    FaDesktop,
    FaCogs,
    FaLock,
    FaChevronLeft,
    FaChevronRight,
} from "react-icons/fa";

const valueProps = [
    {
        icon: <FaKey />,
        title: "Data Ownership",
        points: [
            "Take full control of your community's data with VHOAS no restrictions.",
            "Export or transfer data for audits, migrations, or backups with ease.",
            "Set custom privacy policies tailored to your community's needs.",
            "Use permission settings to ensure only trusted members access sensitive info.",
        ],
    },
    {
        icon: <FaDesktop />,
        title: "Intuitive Platform",
        points: [
            "Manage all HOA tasks in one centralized, easy-to-use platform.",
            "Access documents, updates, and communications from a unified dashboard.",
            "Upload files with drag-and-drop and check real-time event updates.",
            "Board members, residents, and admins collaborate seamlessly.",
        ],
    },
    {
        icon: <FaCogs />,
        title: "Automation",
        points: [
            "Auto-track rule violations and send notices no Tempo manual follow-ups.",
            "Schedule dues collection and payment reminders automatically.",
            "Manage maintenance requests with automated vendor workflows.",
            "Receive alerts and updates to stay informed and save time.",
        ],
    },
    {
        icon: <FaLock />,
        title: "Security",
        points: [
            "256-bit encryption secures all transmitted and stored data.",
            "Data backed up every 8 hours on AWS, no fear of loss.",
            "Multi-factor authentication (2FA) prevents unauthorized access.",
            "Compliant with GDPR, CCPA, and regularly audited for safety.",
        ],
    },
];

const ValueProposition = () => {
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [isMobile, setIsMobile] = useState(false);

    // Check if mobile screen
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Group items into pages based on screen size
    const itemsPerPage = isMobile ? 1 : 2;
    const pages = [];
    for (let i = 0; i < valueProps.length; i += itemsPerPage) {
        pages.push(valueProps.slice(i, i + itemsPerPage));
    }

    const totalPages = pages.length;

    const handleDotClick = (index) => {
        setCurrentPageIndex(index);
    };

    const handlePrevClick = () => {
        setCurrentPageIndex((prevIndex) =>
            prevIndex === 0 ? totalPages - 1 : prevIndex - 1
        );
    };

    const handleNextClick = () => {
        setCurrentPageIndex((prevIndex) =>
            prevIndex === totalPages - 1 ? 0 : prevIndex + 1
        );
    };

    return (
        <section className="p-4 sm:p-8 md:p-12 lg:p-20 text-center bg-[#fafbfc]">
            <h2 className="text-2xl sm:text-3xl md:text-4xl mb-6 md:mb-8 font-bold bg-gradient-to-r from-[#2731a3] to-[#1a2280] bg-clip-text text-transparent">
                Value Proposition
            </h2>
            <h4 className="text-gray-600 max-w-3xl mx-auto text-sm sm:text-base md:text-lg mb-8 md:mb-10 font-medium px-2 leading-relaxed">
                VHOAS is a next-gen HOA platform that gives communities control over
                their data, simplifies operations with AI, and centralizes everything in
                one intuitive platform.
            </h4>

            <div className="relative max-w-7xl mx-auto">
                {/* Navigation Arrows */}
                <button
                    onClick={handlePrevClick}
                    className="absolute left-0 sm:left-2 md:left-4 top-1/2 -translate-y-1/2 text-blue-600 text-xl sm:text-2xl md:text-3xl z-20 hover:text-blue-800 transition-colors bg-white/80 rounded-full p-2 shadow-md"
                >
                    <FaChevronLeft />
                </button>

                <button
                    onClick={handleNextClick}
                    className="absolute right-0 sm:right-2 md:right-4 top-1/2 -translate-y-1/2 text-blue-600 text-xl sm:text-2xl md:text-3xl z-20 hover:text-blue-800 transition-colors bg-white/80 rounded-full p-2 shadow-md"
                >
                    <FaChevronRight />
                </button>

                <div className="overflow-hidden mx-8 sm:mx-10 md:mx-12">
                    <div
                        className="flex transition-transform duration-500 ease-in-out"
                        style={{ transform: `translateX(-${currentPageIndex * 100}%)` }}
                    >
                        {pages.map((page, pageIndex) => (
                            <div
                                key={pageIndex}
                                className={`min-w-full flex justify-center items-center gap-4 md:gap-6 px-2 ${isMobile ? 'flex-col' : 'flex-row'
                                    }`}
                            >
                                {page.map((prop, itemIndex) => (
                                    <div
                                        key={itemIndex}
                                        className={`flex bg-white border border-blue-700/20 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden ${isMobile
                                                ? 'w-full max-w-sm h-auto min-h-[280px] flex-col'
                                                : 'w-1/2 h-80 flex-row'
                                            }`}
                                    >
                                        {/* Icon Side */}
                                        <div className={`bg-[#f8f8ff] flex items-center justify-center text-[#2731a3] ${isMobile
                                                ? 'w-full h-20 p-4 text-3xl'
                                                : 'w-1/3 p-6 text-4xl md:text-5xl lg:text-6xl'
                                            }`}>
                                            {prop.icon}
                                        </div>

                                        {/* Text Side */}
                                        <div className={`text-left flex flex-col justify-center ${isMobile
                                                ? 'w-full p-4 pb-6'
                                                : 'w-2/3 p-4 md:p-6'
                                            }`}>
                                            <h3 className={`font-semibold text-[#2731a3] mb-3 ${isMobile
                                                    ? 'text-lg text-center'
                                                    : 'text-lg md:text-xl'
                                                }`}>
                                                {prop.title}
                                            </h3>
                                            <ul className={`list-disc pl-5 text-gray-700 space-y-1 md:space-y-2 ${isMobile
                                                    ? 'text-sm'
                                                    : 'text-xs sm:text-sm md:text-base'
                                                }`}>
                                                {prop.points.map((point, i) => (
                                                    <li key={i} className="leading-relaxed">{point}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Dots */}
                <div className="flex justify-center gap-2 mt-6 md:mt-8">
                    {pages.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => handleDotClick(index)}
                            className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${currentPageIndex === index
                                    ? "bg-[#2731a3] scale-125"
                                    : "bg-gray-400 hover:bg-gray-500"
                                }`}
                        ></button>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default ValueProposition;